import {CopilotAssignmentModal} from '@github-ui/agent-sessions/components/copilot-assignment/CopilotAssignmentModal'
import type {SelectedAgent} from '@github-ui/agent-sessions/types/session'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import {CommentBox, type CommentBoxHandle} from '@github-ui/comment-box/CommentBox'
import type {Subject} from '@github-ui/comment-box/subject'
import {ContributorFooter} from '@github-ui/contributor-footer/ContributorFooter'
import {COPILOT_PATH} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {CopilotGenerateDescriptionPanel} from '@github-ui/copilot-generate-description-panel'
import {useCopilotGeneratePanel} from '@github-ui/copilot-generate-description-panel/use-copilot-generate-panel'
import {validateIssueBody, validateIssueTitle} from '@github-ui/entity-validators'
// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {IssueFieldSelection} from '@github-ui/issue-fields-shared/issue-field'
import {getIssueFieldValue, toIssueFieldType} from '@github-ui/issue-fields-shared/issue-field'
import {IssueFormElements} from '@github-ui/issue-form/IssueFormElements'
import type {ChangeTrackingProps, IssueFormElementRef, IssueFormRef} from '@github-ui/issue-form/Types'
import {getIssueFieldSelectionValue} from '@github-ui/issue-metadata/IssueFieldWithUnsavedValue'
import {commitUpdateIssueProjectsMutation} from '@github-ui/item-picker/commitUpdateIssueProjectsMutation'
import type {RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import type {ProjectPickerBaseProject as ProjectPickerProject} from '@github-ui/item-picker-project'
import {MarkdownEditor, type ViewMode} from '@github-ui/markdown-editor'
import type {SafeHTMLString} from '@github-ui/safe-html'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {ScopedCommands} from '@github-ui/ui-commands'
import {useAnalytics} from '@github-ui/use-analytics'
import {useNavigate} from '@github-ui/use-navigate'
import {AlertIcon, ArrowUpRightIcon, CopilotIcon, PencilAiIcon} from '@primer/octicons-react'
import {Banner, Dialog, Flash} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {AriaAlert} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {
  type FormEvent,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {useRelayEnvironment} from 'react-relay'
import type {Environment} from 'relay-runtime'

import {ERRORS} from './constants/errors'
import {LABELS} from './constants/labels'
import {useIssueCreateConfigContext} from './contexts/IssueCreateConfigContext'
import {useIssueCreateDataContext} from './contexts/IssueCreateDataContext'
import {CopilotGenerateDescriptionDialog} from './CopilotGenerateDescriptionDialog'
import styles from './CreateIssueForm.module.css'
import {CreateIssueFormTitle} from './CreateIssueFormTitle'
import {useIssueFormSecondaryQuery} from './hooks/use-issue-form-secondary-query'
import {useSemanticSearchAnalytics} from './hooks/use-semantic-search-analytics'
import {useShouldShowCopilotFunctionalityInIssues} from './hooks/use-should-show-copilot-functionality-in-issues'
import type {DuplicateDetectionState} from './hooks/use-similar-issues'
import {MetadataSelectors} from './metadata/MetadataSelectors'
import type {CreateIssueInput} from './mutations/__generated__/createIssueMutation.graphql'
import {commitCreateIssueMutation} from './mutations/create-issue-mutation'
import {UserRestrictedView} from './UserRestrictedView'
import {DisplayMode} from './utils/display-mode'
import {instanceOfIssueFormData, type IssueCreatePayload, IssueCreationKind, type OnCreateProps} from './utils/model'
import {handleIssueRelationships} from './utils/relationship-helpers'
import {getPotentialFormDefaultValuesFromUrl} from './utils/urls'

export type CreateIssueCallbackProps = {
  onCreateSuccess: ({issue, createMore}: OnCreateProps) => void
  onCreateError: (error: Error) => void
  onCancel: () => void
  onBeforeCreate?: (issueBody: string) => Promise<string>
} & ChangeTrackingProps

const EMPTY_ORG_PINNED_FIELDS: readonly IssueFieldSelection[] = []

export type CreateIssueFormProps = {
  repository: RepositoryPickerRepository
  selectedTemplate?: IssueCreatePayload
  title: string
  setTitle: (title: string) => void
  body: string
  setBody: (body: string) => void
  clearOnCreate: () => void
  focusTitleInput?: boolean
  footer?: React.ReactElement
  issueFormRef: RefObject<IssueFormRef | null>
  defaultValuesById?: Record<string, string>
  showUserRestrictedView?: boolean
  /** Duplicate detection state from useSimilarIssues hook */
  duplicateDetection?: DuplicateDetectionState
} & CreateIssueCallbackProps

export const CreateIssueForm = ({
  repository,
  title,
  setTitle,
  body,
  setBody,
  clearOnCreate,
  onCreateSuccess,
  onCreateError,
  selectedTemplate,
  footer,
  issueFormRef,
  onBeforeCreate,
  defaultValuesById,
  showUserRestrictedView = false,
  onFieldChange,
  duplicateDetection,
}: CreateIssueFormProps): React.ReactElement => {
  const {
    optionConfig,
    createMore,
    createMoreCreatedPath,
    setCreateMoreCreatedPath,
    isSubmitting,
    setIsSubmitting,
    onCreateAction,
    setIsFileUploading,
    displayMode,
    isCreatingSubIssueFromProject,
  } = useIssueCreateConfigContext()
  const showMetadataSidepanel = !optionConfig.insidePortal
  const titleInputRef = useRef<HTMLInputElement>(null)

  // Lazily generate clientMutationId for idempotency to prevent duplicate issue creation
  const clientMutationIdRef = useRef<string | undefined>(undefined)

  const [viewMode, setViewMode] = useState<ViewMode>()
  const [titleValidationResult, setTitleValidationResult] = useState<string | undefined>(undefined)
  const [bodyValidationResult, setBodyValidationResult] = useState<string | undefined>(undefined)
  const [submissionErrors, setSubmissionErrors] = useState<string | null>(null)
  const secondaryIssueFormData = useIssueFormSecondaryQuery({repoId: repository.id})
  const errorBanner = useRef<HTMLDivElement>(null)
  const environment = useRelayEnvironment()
  const {sendAnalyticsEvent} = useAnalytics()
  const {addToast} = useToastContext()
  const {
    labels,
    assignees,
    replaceAssignees,
    projects,
    milestone,
    issueType,
    issueFields,
    parentIssueId,
    blockedByIssueIds,
    blockingIssueIds,
    usedStorageKeyPrefix,
    isCopilotAssignmentDialogOpen,
    setIsCopilotAssignmentDialogOpen,
    copilotAssignmentAttributes,
    setCopilotAssignmentAttributes,
    setIssueFormBody,
  } = useIssueCreateDataContext()

  // Extract duplicate detection callbacks (may be undefined if feature disabled)
  const resetDuplicateDetection = duplicateDetection?.resetForNewSession
  const trackDuplicateDetectionIssueCreated = duplicateDetection?.trackIssueCreated

  const shouldShowCopilotFunctionality = useShouldShowCopilotFunctionalityInIssues({
    additionalConditions: [selectedTemplate ? !instanceOfIssueFormData(selectedTemplate.data) : true],
    repositoryVisibility: repository.visibility,
    userCanPushToRepo: repository.viewerCanPush,
  })

  const postSemanticSearchAnalytics = useSemanticSearchAnalytics(repository)

  useEffect(() => {
    if (shouldShowCopilotFunctionality) {
      sendAnalyticsEvent('analytics.view', 'ISSUE_CREATE_NEW_ISSUE_WITH_COPILOT_BUTTON_VIEW', {
        repoNWO: `${repository.owner.login}/${repository.name}`,
      })
    }
  }, [repository.name, repository.owner.login, sendAnalyticsEvent, shouldShowCopilotFunctionality])

  // When Copilot assignment attributes are set after modal submission, trigger issue creation
  const pendingCreationRef = useRef(false)
  useEffect(() => {
    if (copilotAssignmentAttributes && pendingCreationRef.current) {
      pendingCreationRef.current = false
      // Trigger the issue creation now that we have the Copilot attributes
      onCreateAction.current?.onCreate(false, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copilotAssignmentAttributes])

  // Reset pendingCreationRef when modal closes without submission
  useEffect(() => {
    if (!isCopilotAssignmentDialogOpen && !copilotAssignmentAttributes) {
      pendingCreationRef.current = false
    }
  }, [isCopilotAssignmentDialogOpen, copilotAssignmentAttributes])

  const restrictedViewContent = (
    <UserRestrictedView
      /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point. */
      reasonHTML={repository.viewerInteractionLimitReasonHTML as SafeHTMLString}
      issuesUrl={`/${repository.owner.login}/${repository.name}/issues`}
    />
  )

  const canIssueType = repository.viewerIssueCreationPermissions.typeable
  const canSetIssueFields = repository.viewerIssueCreationPermissions.canSetIssueFields

  const formatFormErrorMessage = (errors: Error[]) => {
    const errorMessages = errors
      .map(err => err?.message)
      .filter(Boolean)
      .join(', ')

    // Some gql errors start with lowercase letters, so normalizing it
    const cleanedErrorMessages = errorMessages ? errorMessages.charAt(0).toUpperCase() + errorMessages.slice(1) : null

    return cleanedErrorMessages ?? ERRORS.createIssueError
  }

  const selectedDefaultValuesById = useMemo(
    () => defaultValuesById || getPotentialFormDefaultValuesFromUrl(),
    [defaultValuesById],
  )

  useEffect(() => {
    if (submissionErrors && submissionErrors.length > 0 && errorBanner?.current) {
      errorBanner.current.focus()
    }
  }, [errorBanner, submissionErrors])

  // Duplicate detection feature - get state from prop
  const isDuplicateDetectionEnabled = duplicateDetection?.isDuplicateDetectionEnabled ?? false
  const isIssueForm = selectedTemplate && instanceOfIssueFormData(selectedTemplate.data)

  const handleFormFieldChangeForDuplicateDetection = useCallback<NonNullable<ChangeTrackingProps['onFieldChange']>>(
    (id, indexOfFormElement, value) => {
      onFieldChange?.(id, indexOfFormElement, value)

      if (isDuplicateDetectionEnabled && issueFormRef.current) {
        queueMicrotask(() => {
          setIssueFormBody(issueFormRef.current?.markdown() ?? '')
        })
      }
    },
    [isDuplicateDetectionEnabled, issueFormRef, setIssueFormBody, onFieldChange],
  )

  useImperativeHandle(
    onCreateAction,
    () => ({
      onCreate: async (isSubmittingArg, createMoreArg) => {
        if (isSubmittingArg) return

        let refToFocusOnError: HTMLInputElement | IssueFormElementRef | undefined = undefined

        const inputValidationResult = validateIssueTitle(title)
        setTitleValidationResult(inputValidationResult.errorMessage)
        if (!inputValidationResult.isValid && titleInputRef.current) {
          refToFocusOnError = titleInputRef.current
        }

        const isUsingIssueForm = selectedTemplate && instanceOfIssueFormData(selectedTemplate.data)
        let issueBodyToSave = body
        if (isUsingIssueForm && issueFormRef.current) {
          const invalidInputs = issueFormRef.current.getInvalidInputs()
          // If there are validation errors, use first input if there's no ref yet
          if (invalidInputs.length > 0) {
            refToFocusOnError = refToFocusOnError || invalidInputs[0]
          } else {
            issueBodyToSave = issueFormRef.current.markdown() ?? ''
          }
        }

        // We validate the length === 0 also for the initial state, in which the validation result will be undefined, too.
        if (
          refToFocusOnError ||
          titleValidationResult !== undefined ||
          (title ?? '').trim().length === 0 ||
          repository === undefined ||
          bodyValidationResult !== undefined
        ) {
          // If there is an input with an error, focus it
          refToFocusOnError?.focus()
          return
        }

        setSubmissionErrors(null)
        setIsSubmitting(true)

        // Check if Copilot is one of the assignees but we don't have assignment attributes yet
        const hasCopilotAssignee = assignees.some(assignee => isCopilot(assignee.login))
        if (hasCopilotAssignee && !copilotAssignmentAttributes) {
          // Show the Copilot assignment modal and return - issue will be created after modal submission
          pendingCreationRef.current = true
          setIsCopilotAssignmentDialogOpen(true)
          setIsSubmitting(false)
          return
        }

        if (onBeforeCreate) {
          issueBodyToSave = await onBeforeCreate(issueBodyToSave)
        }

        let issueTemplate = undefined
        if (
          selectedTemplate &&
          (selectedTemplate.kind === IssueCreationKind.IssueTemplate ||
            selectedTemplate.kind === IssueCreationKind.IssueForm)
        ) {
          issueTemplate = selectedTemplate.name
        }

        const input: CreateIssueInput = {
          repositoryId: repository.id,
          title: title ? title.trim() : '',
          body: issueBodyToSave,
          labelIds: labels.length > 0 ? labels.map(label => label.id) : undefined,
          assigneeIds: assignees.length > 0 ? assignees.map(assignee => assignee.id) : undefined,
          milestoneId: milestone?.id,
          issueTypeId: canIssueType || issueTemplate ? issueType?.id : null,
          issueFields: canSetIssueFields
            ? issueFields
                .map(field => {
                  if (field.field.dataType === undefined || field.field.id === undefined) return null
                  const value = getIssueFieldSelectionValue(toIssueFieldType(field.field.dataType), field.value)
                  return {
                    fieldId: field.field.id,
                    ...getIssueFieldValue(toIssueFieldType(field.field.dataType), value),
                  }
                })
                .filter(v => !!v)
            : null,
          issueTemplate,
          isDuplicated: displayMode === DisplayMode.IssueDuplication,
          parentIssueId,
          // Lazily generate clientMutationId on first use (client-side only) to avoid SSR issues
          clientMutationId: (clientMutationIdRef.current ??= crypto.randomUUID()),
          copilotAssignment: copilotAssignmentAttributes ?? undefined,
        }

        // For sub-issues, close the dialog immediately (optimistic update will show the item)
        // For regular issues, wait for the mutation to complete before calling onCreateSuccess
        // For sub-issues created from a project, wait for the mutation to complete so the issue can be added to the project
        const closeImmediately = !isCreatingSubIssueFromProject && !!parentIssueId && !createMoreArg

        commitCreateIssueMutation({
          environment,
          input,
          onError: (error: Error) => {
            reportError(formatError(error.message))
            onCreateError(error)
            setIsSubmitting(false)

            let errorMessage = ERRORS.createIssueError

            if (error.cause && Array.isArray(error.cause) && error.cause.length > 0) {
              errorMessage = formatFormErrorMessage(error.cause)
            }

            // For sub-issues that closed immediately, show a toast since the dialog is gone
            if (closeImmediately) {
              // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
              addToast({
                type: 'error',
                message: errorMessage,
              })
            } else {
              setSubmissionErrors(errorMessage)
            }
          },
          onCompleted: response => {
            setIsSubmitting(false)
            if (!response.createIssue?.issue) {
              response.createIssue?.errors.map(e => reportError(formatError(e.message)))

              let errorMessage = ERRORS.createIssueError
              if (response.createIssue?.errors && response.createIssue?.errors.length > 0) {
                errorMessage = formatFormErrorMessage(response.createIssue.errors as Error[])
              }

              // For sub-issues that closed immediately, show a toast since the dialog is gone
              if (closeImmediately) {
                // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
                addToast({
                  type: 'error',
                  message: errorMessage,
                })
              } else {
                setSubmissionErrors(errorMessage)
              }
              return
            }

            const newIssue = response.createIssue.issue

            sendAnalyticsEvent('analytics.click', 'ISSUE_CREATE_NEW_ISSUE_BUTTON', {
              issueId: newIssue.id,
              issueNumber: newIssue.number,
              issueNWO: `${newIssue.repository.owner.login}/${newIssue.repository.name}`,
            })

            // Track duplicate detection interaction when issue is created
            if (isDuplicateDetectionEnabled) {
              trackDuplicateDetectionIssueCreated?.()
            }

            if (projects.length > 0) {
              assignIssueToProjects(newIssue.id, projects, environment)
            }

            if (blockedByIssueIds.length > 0 || blockingIssueIds.length > 0) {
              handleIssueRelationships(environment, newIssue.id, blockedByIssueIds, blockingIssueIds)
            }

            // Generate a new clientMutationId for the next submission to ensure idempotency
            clientMutationIdRef.current = crypto.randomUUID()

            if (createMoreArg) {
              setViewMode('edit')
              setCreateMoreCreatedPath({...createMoreCreatedPath, number: newIssue.number})

              if (isDuplicateDetectionEnabled) {
                resetDuplicateDetection?.()
                setIssueFormBody('') // Clear stale form body to prevent incorrect duplicate suggestions
              }
            } else {
              clearOnCreate()
            }

            // Only call onCreateSuccess here if we didn't already close immediately
            if (!closeImmediately) {
              onCreateSuccess({issue: newIssue, createMore: createMoreArg})
            }
          },
        })

        // For sub-issues, close immediately without waiting for the mutation to complete
        if (closeImmediately) {
          clearOnCreate()
          // Pass a minimal issue object since the dialog just needs to close
          // The optimistic update already shows the item in the list
          onCreateSuccess({
            issue: {
              id: clientMutationIdRef.current ?? '',
              number: 0,
              repository: {
                name: repository.name,
                owner: {login: repository.owner.login},
                databaseId: undefined,
                id: '',
              },
              databaseId: undefined,
              title: '',
              url: '',
              ' $fragmentSpreads': {} as never,
            },
            createMore: false,
          })
        }
      },
    }),
    [
      title,
      selectedTemplate,
      body,
      issueFormRef,
      titleValidationResult,
      repository,
      bodyValidationResult,
      setIsSubmitting,
      onBeforeCreate,
      labels,
      assignees,
      milestone?.id,
      canIssueType,
      issueType?.id,
      canSetIssueFields,
      issueFields,
      displayMode,
      parentIssueId,
      blockedByIssueIds,
      blockingIssueIds,
      copilotAssignmentAttributes,
      setIsCopilotAssignmentDialogOpen,
      environment,
      onCreateError,
      sendAnalyticsEvent,
      projects,
      onCreateSuccess,
      setCreateMoreCreatedPath,
      createMoreCreatedPath,
      clearOnCreate,
      addToast,
      isCreatingSubIssueFromProject,
      isDuplicateDetectionEnabled,
      resetDuplicateDetection,
      trackDuplicateDetectionIssueCreated,
      setIssueFormBody,
    ],
  )

  const handleTitleChange = (e: FormEvent<HTMLInputElement>) => {
    const inputTitle = e.currentTarget.value
    validateAndSetTitle(inputTitle)
  }

  const validateAndSetTitle = (newTitle: string) => {
    const inputValidationResult = validateIssueTitle(newTitle)
    // when title changes, check if we need to clear validation error
    // validation error are only added when user tries to create the issue
    if (inputValidationResult.isValid) {
      setTitleValidationResult(undefined)
    }
    setTitle(newTitle)
    postSemanticSearchAnalytics(newTitle, body)
  }
  const handleBodyChange = (inputBody: string) => {
    validateAndSetBody(inputBody)
  }
  const validateAndSetBody = (newBody: string) => {
    const inputValidationResult = validateIssueBody(newBody)

    setBodyValidationResult(inputValidationResult.errorMessage)
    setBody(newBody)

    postSemanticSearchAnalytics(title, newBody)
  }

  const subject = useMemo<Subject | undefined>(() => {
    if (repository) {
      return {
        type: 'issue',
        repository: {
          databaseId: repository.databaseId!,
          nwo: `${repository.owner.login}/${repository.name}`,
          slashCommandsEnabled: repository.slashCommandsEnabled,
        },
      }
    }
  }, [repository])

  const validationErrorId = useId()

  const footerButtons = []
  const navigate = useNavigate()
  const copilotCTARef = useRef<HTMLButtonElement>(null)
  const assigneesButtonRef = useRef<HTMLButtonElement | null>(null)
  const commentBoxRef = useRef<CommentBoxHandle>(null)
  const [isCopilotCTADialogOpen, setIsCopilotCTADialogOpen] = useState(false)
  const [isCopilotGenerateDialogOpen, setIsCopilotGenerateDialogOpen] = useState(false)

  // Determines if we should show the footer button to open a dialog to edit with copilot. Outdated functionality
  // based on another teams work. When we staff-ship write_issue_body_with_copilot, we can remove this.
  const isCreateIssueDescriptionWithCopilotEnabled = isFeatureEnabled('create_issue_description_with_copilot')
  const shouldUseDialogCopilotWriting = isCreateIssueDescriptionWithCopilotEnabled && shouldShowCopilotFunctionality

  // Use the Copilot panel hook to manage inline copilot writing functionality
  const {
    isPanelOpen: isCopilotGeneratePanelOpen,
    isGenerating: isCopilotGenerating,
    shouldShowCopilot: shouldUseInlineCopilotWriting,
    togglePanel: toggleCopilotPanel,
    handleInputFocus: handleCommentBoxInputFocus,
    commands: copilotPanelCommands,
    panelProps: copilotPanelProps,
    containerStyle: copilotContainerStyle,
  } = useCopilotGeneratePanel({
    editorRef: commentBoxRef,
    body,
    onChange: (newBody, newTitle) => {
      validateAndSetBody(newBody)
      if (newTitle !== undefined) {
        validateAndSetTitle(newTitle)
      }
    },
    shouldShowCopilotFunctionality,
    repoOwner: repository.owner.login,
    repoName: repository.name,
    title,
    generateTitle: true,
    commandId: 'issue-create:open-copilot-panel',
    canToggle: viewMode !== 'preview',
  })

  useEffect(() => {
    const assigneesButton = document.getElementById('assignees-select-menu') as HTMLButtonElement | null
    if (assigneesButton) {
      assigneesButtonRef.current = assigneesButton
    }
  }, [])

  const navigateToCopilot = () => {
    sendAnalyticsEvent('analytics.click', 'ISSUE_CREATE_NEW_ISSUE_WITH_COPILOT_BUTTON', {
      repoNWO: `${repository.owner.login}/${repository.name}`,
    })
    const prompt = `Create an issue in ${repository.nameWithOwner} to `
    const params = new URLSearchParams({prompt})
    navigate(`${COPILOT_PATH}?${params.toString()}`)
  }

  const handleGenerateDescription = (generatedTitle: string, generatedDescription: string) => {
    setTitle(generatedTitle)
    setBody(generatedDescription)
    setIsCopilotGenerateDialogOpen(false)
  }

  const handleCTAOnClick = () => {
    if (shouldUseInlineCopilotWriting) {
      toggleCopilotPanel()
    } else if (isCreateIssueDescriptionWithCopilotEnabled) {
      setIsCopilotGenerateDialogOpen(true)
    } else {
      if (body.length || title.length) {
        setIsCopilotCTADialogOpen(true)
      } else {
        navigateToCopilot()
      }
    }
  }

  // If not using the panel, we need the footer button on the editor
  if (shouldShowCopilotFunctionality && !shouldUseInlineCopilotWriting) {
    footerButtons.push(
      <MarkdownEditor.FooterButton
        ref={copilotCTARef}
        key="create-issue-with-copilot"
        variant="invisible"
        leadingVisual={shouldUseDialogCopilotWriting ? CopilotIcon : ArrowUpRightIcon}
        size="small"
        className={styles.footerButton}
        onClick={() => {
          handleCTAOnClick()
        }}
      >
        {LABELS.copilotCTAButton}
      </MarkdownEditor.FooterButton>,
    )
  }

  const handleCloseCopilotModal = () => {
    setIsCopilotAssignmentDialogOpen(false)
  }

  const onCopilotModalSubmit = (
    repositoryId: string,
    branch?: string,
    instructions?: string,
    selectedAgent?: SelectedAgent,
    model?: string,
  ) => {
    setCopilotAssignmentAttributes({
      targetRepositoryId: repositoryId,
      baseRef: branch,
      customInstructions: instructions,
      customAgent: selectedAgent?.subAgentSlug,
      model,
    })

    // Update the assignee to reflect the selected agent instead of always showing "Copilot"
    if (selectedAgent?.displayName && selectedAgent?.login && selectedAgent?.botGlobalRelayId) {
      const isDefaultCopilot = isCopilot(selectedAgent.login)
      if (!isDefaultCopilot) {
        const agentAssignee: (typeof assignees)[number] = {
          id: selectedAgent.botGlobalRelayId ?? '',
          login: selectedAgent.login,
          name: selectedAgent.displayName,
          avatarUrl: selectedAgent.avatarUrl ?? '',
          __typename: 'Bot',
          isCopilot: false,
          isAgent: true,
          displayName: selectedAgent.displayName,
        }
        const updatedAssignees = assignees.map(a => (isCopilot(a.login) ? agentAssignee : a))
        replaceAssignees(updatedAssignees)
      }
    }

    handleCloseCopilotModal()
  }

  const mainContent = showUserRestrictedView ? (
    restrictedViewContent
  ) : (
    <>
      {isCopilotCTADialogOpen && (
        <Dialog
          title={LABELS.copilotCTADialogTitle}
          onClose={() => setIsCopilotCTADialogOpen(false)}
          footerButtons={[
            {
              buttonType: 'default',
              content: LABELS.copilotCTADialogCancelButton,
              onClick: () => setIsCopilotCTADialogOpen(false),
            },
            {
              buttonType: 'primary',
              content: LABELS.copilotCTADialogContinueButton,
              onClick: navigateToCopilot,
            },
          ]}
          returnFocusRef={copilotCTARef}
        >
          {LABELS.copilotCTADialogDescription}
        </Dialog>
      )}
      {!shouldUseInlineCopilotWriting && isCreateIssueDescriptionWithCopilotEnabled && (
        <CopilotGenerateDescriptionDialog
          isOpen={isCopilotGenerateDialogOpen}
          onClose={() => setIsCopilotGenerateDialogOpen(false)}
          onGenerate={handleGenerateDescription}
          repoOwner={repository.owner.login}
          repoName={repository.name}
          title={title}
          body={body}
          returnFocusRef={copilotCTARef}
        />
      )}
      {submissionErrors && (
        <Banner
          ref={errorBanner}
          title="Error"
          description={<AriaAlert>{submissionErrors}</AriaAlert>}
          variant="critical"
          className={styles.errorBanner}
          role="alert"
        />
      )}
      <CopilotAssignmentModal
        isOpen={isCopilotAssignmentDialogOpen}
        onClose={handleCloseCopilotModal}
        onSubmit={onCopilotModalSubmit}
        repository={{
          name: repository.name,
          owner: {
            login: repository.owner.login,
          },
        }}
        returnFocusRef={assigneesButtonRef}
      />
      <CreateIssueFormTitle
        title={title}
        titleInputRef={titleInputRef}
        handleTitleChange={handleTitleChange}
        titleValidationResult={titleValidationResult}
      />
      {subject && !isIssueForm && (
        <ScopedCommands commands={copilotPanelCommands}>
          <div className={styles.commentBox} style={copilotContainerStyle}>
            <CommentBox
              ref={commentBoxRef}
              subject={subject}
              label={LABELS.issueCreateBodyLabel}
              showLabel
              placeholder={LABELS.issueBodyPlaceholder}
              viewMode={viewMode}
              onChangeViewMode={setViewMode}
              /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point. */
              value={body as SafeHTMLString}
              onSave={() => onCreateAction?.current?.onCreate(isSubmitting, createMore)}
              onChange={handleBodyChange}
              saveButtonTrailingIcon={false}
              minHeightLines={optionConfig.insidePortal ? 14 : 20}
              aria-describedby={bodyValidationResult ? validationErrorId : undefined}
              setIsFileUploading={setIsFileUploading}
              toolbarButtons={
                shouldUseInlineCopilotWriting ? (
                  <MarkdownEditor.ToolbarButton
                    variant="invisible"
                    icon={PencilAiIcon}
                    size="small"
                    className={styles.copilotWriteButton}
                    onClick={toggleCopilotPanel}
                    aria-pressed={isCopilotGeneratePanelOpen}
                    aria-label={LABELS.copilotCTAButton}
                  />
                ) : null
              }
              footerButtons={footerButtons}
              disabled={isCopilotGenerating}
              generatingText={isCopilotGenerating}
              onInputFocus={handleCommentBoxInputFocus}
            />
            {shouldUseInlineCopilotWriting && isCopilotGeneratePanelOpen && viewMode !== 'preview' && (
              <CopilotGenerateDescriptionPanel {...copilotPanelProps} />
            )}
            {bodyValidationResult && (
              <Flash variant="danger" id={validationErrorId}>
                <Octicon icon={AlertIcon} />
                {bodyValidationResult}
              </Flash>
            )}
          </div>
        </ScopedCommands>
      )}
      {subject && isIssueForm && instanceOfIssueFormData(selectedTemplate.data) && (
        <IssueFormElements
          outputRef={issueFormRef}
          issueFormRef={selectedTemplate.data}
          subject={subject}
          sessionStorageKey={usedStorageKeyPrefix}
          defaultValuesById={selectedDefaultValuesById}
          onSave={() => onCreateAction?.current?.onCreate(isSubmitting, createMore)}
          setIsFileUploading={setIsFileUploading}
          onFieldChange={isDuplicateDetectionEnabled ? handleFormFieldChangeForDuplicateDetection : onFieldChange}
        />
      )}
    </>
  )

  const metadataContent = (
    <>
      <MetadataSelectors
        orgPinnedFields={secondaryIssueFormData?.orgPinnedFields ?? EMPTY_ORG_PINNED_FIELDS}
        isPinnedFieldsLoading={secondaryIssueFormData === null}
      />
      {secondaryIssueFormData && (
        <ContributorFooter
          supportFileUrl={secondaryIssueFormData.supportFileUrl ?? undefined}
          codeOfConductFileUrl={secondaryIssueFormData.codeOfConductFileUrl ?? undefined}
          securityPolicyUrl={secondaryIssueFormData.securityPolicyUrl ?? undefined}
          contributingFileUrl={secondaryIssueFormData.contributingFileUrl ?? undefined}
        />
      )}
    </>
  )

  const footerContent = showUserRestrictedView ? null : (
    <>
      {footer && (
        <div
          className={clsx(
            styles.footerArea,
            isIssueForm ? styles.footerAreaIssueForm : styles.footerAreaDefault,
            !optionConfig.insidePortal && isDuplicateDetectionEnabled && styles.duplicateDetectionStickyFooter,
          )}
        >
          {footer}
        </div>
      )}
    </>
  )

  if (showMetadataSidepanel) {
    return (
      <div className={styles.CreateIssueFormWrapper}>
        <div className={clsx('width-fit', styles.formGridContainer, styles.formGridWithMetadata)}>
          <div className={styles.mainContentSection}>{mainContent}</div>
          <div className={styles.metadataSection}>{metadataContent}</div>
          {footerContent}
        </div>
      </div>
    )
  } else {
    return (
      <>
        {mainContent}
        {metadataContent}
        {footerContent}
      </>
    )
  }
}

function assignIssueToProjects(issueId: string, projects: ProjectPickerProject[], environment: Environment) {
  for (const project of projects) {
    commitUpdateIssueProjectsMutation({environment, issueId, projectId: project.id})
  }
}

function formatError(message: string) {
  return new Error(`Issue create mutation failed with error: ${message}`)
}
